import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { IncidentService } from './incident.service';
import { Document, UploadingFile } from '../models/incident';

export interface QueuedImage {
  id?: number;
  incidentId: number;
  fileBase64: string;
  name: string;
  dtCreated: Date;
  documentClassificationId: number;
  createdByUserId: number;
  status: 'pending' | 'uploading' | 'failed';
  retryCount: number;
  description?: string;
  subCategoryId?: number;
}

const DB_NAME = 'MaintenanceAppDB';
const DB_VERSION = 1;
const STORE_NAME = 'imageQueue';

@Injectable({
  providedIn: 'root'
})

export class ImageQueueService {

  public uploadSuccess$ = new Subject<{ localId: number, document: Document }>();
  public uploadFailed$ = new Subject<{ localId: number }>();
  private isProcessing = false;
  
  constructor(private incidentService: IncidentService) {}

  private openDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
          store.createIndex('incidentId', 'incidentId', { unique: false });
        }
      }
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    })
  }
  
  public async addImage(image: Omit<QueuedImage, 'id' | 'status' | 'retryCount'>): Promise<number> {
    const db = await this.openDb();

    return new Promise((resolve, reject) => {
      const incomingImage = { ...image, status: 'pending', retryCount: 0 };
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.add(incomingImage);

      request.onsuccess = () => resolve(request.result as number);
      request.onerror = () => reject(request.error);
    })
  }

  public async getPendingImagesForIncident(incidentId: number): Promise<QueuedImage[]> {
    const db = await this.openDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const index = transaction.objectStore(STORE_NAME).index('incidentId');
      const request = index.getAll(incidentId);

      request.onsuccess = () => {
        const images = request.result as QueuedImage[];
        resolve(images.filter(img => img.status === 'pending' || img.status === 'failed'));
        
      }
      request.onerror = () => reject(request.error);
    })
  }

  public async resetStuckUploads(incidentId: number): Promise<void> {
    const db = await this.openDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const index = transaction.objectStore(STORE_NAME).index('incidentId');
      const request = index.getAll(incidentId);

      request.onsuccess = () => {
        const images = request.result as QueuedImage[];
        const stuckImages = images.filter(img => img.status === 'uploading');
        if (stuckImages.length === 0) {
          resolve();
          return;
        }
        let remaining = stuckImages.length;
        stuckImages.forEach(img => {
          img.status = 'pending';
          const updateRequest = transaction.objectStore(STORE_NAME).put(img);
          updateRequest.onsuccess = () => {
            remaining--;
            if (remaining === 0) {
              resolve();
            }
          }
          updateRequest.onerror = () => reject(updateRequest.error);
        });
      }
     request.onerror = () => reject(request.error);
    })
  }

  public async removeImage(id: number): Promise<void> {
    const db = await this.openDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const request = transaction.objectStore(STORE_NAME).delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    })
  }

  private async updateImageStatus(id: number, status: 'pending' | 'uploading' | 'failed', incrementRetryCount: boolean = false): Promise<void> {
    const db = await this.openDb();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const getRequest = store.get(id);
      getRequest.onsuccess = () => {
        const image = getRequest.result as QueuedImage;
        if (image) {
          image.status = status;
          if (incrementRetryCount) {
            image.retryCount = (image.retryCount || 0) + 1;
          }
          const updateRequest = store.put(image);
          updateRequest.onsuccess = () => resolve();
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          reject(new Error('Image not found'));
        }
      }
      getRequest.onerror = () => reject(getRequest.error);
    })
  }

  public async processQueue(incidentId: number): Promise<void> {
    if (this.isProcessing) {
      return;
    }
    this.isProcessing = true;

    try {
      const pendingImages = await this.getPendingImagesForIncident(incidentId);
      for (const image of pendingImages) {
        const localId = image.id!;
        image.status = 'uploading';
        await this.updateImageStatus(localId, 'uploading');
        const uploadingFile: UploadingFile = {
          base64: image.fileBase64,
          name: image.name,
          incidentId: image.incidentId,
          documentClassificationId: image.documentClassificationId,
          createdByUserId: image.createdByUserId,
          subCategoryId: image.subCategoryId,
          description: image.description   
      };
      await new Promise<void>((resolve) => {
        this.incidentService.uploadFile(uploadingFile, incidentId).subscribe({
          next: async (document) => {
            await this.removeImage(localId);
            this.uploadSuccess$.next({ localId, document });
            resolve();
          },
          error: async () => {
            await this.updateImageStatus(localId, 'failed', true);
            this.uploadFailed$.next({ localId });
            resolve();
          }   
      });
      }) 
    }
  } finally {
      this.isProcessing = false;
  }
  }

  public async processQueueNoToken(incidentId: number): Promise<void> {
    if (this.isProcessing) {
      return;
    }
    this.isProcessing = true;

    try {
      const pendingImages = await this.getPendingImagesForIncident(incidentId);
      for (const image of pendingImages){
        const localId = image.id!;
        await this.updateImageStatus(localId, 'uploading');
        const uploadingFile: UploadingFile = {
          base64: image.fileBase64,
          name: image.name,
          incidentId: image.incidentId,
          documentClassificationId: image.documentClassificationId,
          createdByUserId: image.createdByUserId,
          subCategoryId: image.subCategoryId,
          description: image.description
        };
        await new Promise<void>((resolve) => {
          this.incidentService.uploadFileNoToken(uploadingFile, incidentId).subscribe({
            next: async (document) => {
              await this.removeImage(localId);
              this.uploadSuccess$.next({ localId, document });
              resolve();
            },
            error: async () => {
              await this.updateImageStatus(localId, 'failed', true);
              this.uploadFailed$.next({ localId });
              resolve();
            }
          });
      });
    }
    } finally {
        this.isProcessing = false;
      }
  }
}
